/**
 * How long the sidebar's running text takes to go round — pure, so it can be
 * tested without a browser. The loop is two identical copies side by side that
 * slide left by exactly one copy's width, then jump back; for that to be
 * seamless every copy must be the SAME width, and wide enough that the box is
 * never left empty.
 */

/** Empty run after the text before it comes round again. */
export const TICKER_GAP_PX = 48;
export const TICKER_SPEED_DEFAULT = 30;
export const TICKER_SPEED_MIN = 5;
export const TICKER_SPEED_MAX = 200;

/**
 * A usable speed whatever was saved. The settings field is a number box whose
 * `onChange` turns a cleared field into 0, and a speed of 0 would divide the
 * duration by zero, so an unusable value falls back rather than clamps.
 */
export function clampTickerSpeed(speed: number): number {
  if (!Number.isFinite(speed) || speed <= 0) return TICKER_SPEED_DEFAULT;
  return Math.min(TICKER_SPEED_MAX, Math.max(TICKER_SPEED_MIN, speed));
}

export interface ITickerTiming {
  /** Width of one copy, in px: text + gap, never narrower than the box. */
  copyW: number;
  /** Seconds for one full loop, so the speed is the same however long the text. */
  durationS: number;
}

/**
 * `textW` is the text on its own, `boxW` the visible window. A SHORT message is
 * padded out to the width of the box so it still travels across it instead of
 * sitting still while its copy loops invisibly. Returns null while nothing has
 * been measured (hidden, or not laid out yet) — the caller leaves the CSS
 * default alone rather than animate by a width of zero.
 */
export function tickerTiming(textW: number, boxW: number, speed: number): ITickerTiming | null {
  if (!(textW > 0) && !(boxW > 0)) return null;
  const copyW = Math.max(Math.ceil(Math.max(textW, 0)) + TICKER_GAP_PX, Math.ceil(Math.max(boxW, 0)));
  return { copyW, durationS: copyW / clampTickerSpeed(speed) };
}
